<template>
  <div class="people-admin">
    <div v-if="loading" class="people-loading">
      <b-spinner small /> {{ $t('people_loading') }}
    </div>

    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>

    <template v-else-if="status">
      <b-alert v-if="operationError" show dismissible variant="danger" @dismissed="operationError = null">
        {{ operationError }}
      </b-alert>

      <b-alert v-if="status.capability.state === 'unavailable'" show variant="warning">
        <strong>{{ $t('people_unavailable') }}</strong>
        <div>{{ status.capability.reason }}</div>
        <small>{{ $t('people_core_safe') }}</small>
      </b-alert>

      <div v-else-if="!status.jobs.enabled" class="people-onboarding">
        <div class="people-onboarding-icon"><i class="bi bi-people-fill" /></div>
        <h3>{{ $t('people_title') }}</h3>
        <p>{{ $t('people_onboarding') }}</p>
        <p class="privacy-note"><i class="bi bi-shield-lock" /> {{ $t('people_privacy') }}</p>
        <b-button variant="primary" size="lg" @click="enable">
          {{ $t('people_enable') }}
        </b-button>
      </div>

      <template v-else>
        <div class="people-toolbar">
          <div>
            <h3>{{ $t('people_title') }}</h3>
            <span class="people-state" :class="stateClass">{{ stateLabel }}</span>
          </div>
          <div class="people-actions">
            <b-button v-if="status.jobs.total === 0" variant="primary" @click="analyze('all')">
              <i class="bi bi-stars" /> {{ $t('people_analyze_all') }}
            </b-button>
            <b-dropdown v-else right variant="outline-primary" :text="$t('people_analyze')">
              <b-dropdown-item @click="analyze('missing')">{{ $t('people_analyze_missing') }}</b-dropdown-item>
              <b-dropdown-item @click="analyze('all')">{{ $t('people_analyze_all') }}</b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="confirmRebuild">{{ $t('people_rebuild') }}</b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="confirmDisable">{{ $t('people_disable') }}</b-dropdown-item>
              <b-dropdown-item variant="danger" @click="confirmErase">{{ $t('people_erase') }}</b-dropdown-item>
            </b-dropdown>
            <b-button variant="outline-secondary" @click="pause(!status.jobs.paused)">
              <i :class="status.jobs.paused ? 'bi bi-play-fill' : 'bi bi-pause-fill'" />
              {{ status.jobs.paused ? $t('people_resume') : $t('people_pause') }}
            </b-button>
            <b-button variant="outline-secondary" @click="undoLast" :disabled="!lastAction">
              <i class="bi bi-arrow-counterclockwise" /> {{ $t('people_undo') }}
            </b-button>
          </div>
        </div>

        <div class="people-metrics">
          <button @click="selectFilter('review')"><strong>{{ status.people.needs_review }}</strong><span>{{ $t('people_needs_review') }}</span></button>
          <button @click="selectFilter('candidate')"><strong>{{ status.people.candidates }}</strong><span>{{ $t('people_unconfirmed') }}</span></button>
          <button @click="selectFilter('confirmed')"><strong>{{ status.people.confirmed }}</strong><span>{{ $t('people_confirmed') }}</span></button>
          <button @click="selectFilter('hidden')"><strong>{{ status.people.hidden }}</strong><span>{{ $t('people_hidden') }}</span></button>
        </div>

        <div v-if="status.jobs.pending || status.jobs.running" class="people-progress">
          <b-progress :value="completedJobs" :max="activeJobs" animated />
          <span>{{ status.jobs.completed }} {{ $t('people_completed') }} · {{ status.jobs.pending }} {{ $t('people_queued') }} · {{ status.jobs.running }} {{ $t('people_running') }}</span>
        </div>

        <div v-if="status.jobs.duration.average_ms != null" class="people-diagnostics">
          <span><i class="bi bi-stopwatch" /> {{ $t('people_average_duration') }}: {{ formatDuration(status.jobs.duration.average_ms) }}</span>
          <span v-if="status.jobs.duration.estimated_remaining_ms != null && (status.jobs.pending || status.jobs.running)">
            {{ $t('people_estimated_remaining') }}: {{ formatDuration(status.jobs.duration.estimated_remaining_ms) }}
          </span>
        </div>

        <b-alert v-if="status.jobs.failed" show variant="warning" class="people-failure-alert">
          <span><strong>{{ status.jobs.failed }}</strong> {{ $t('people_failed_analyses') }}</span>
          <b-button size="sm" variant="outline-warning" @click="openFailures">{{ $t('people_view_failures') }}</b-button>
        </b-alert>

        <div class="people-filterbar">
          <b-button-group>
            <b-button v-for="filter in filters" :key="filter.id"
              :variant="activeFilter === filter.id ? 'primary' : 'outline-secondary'"
              @click="selectFilter(filter.id)">{{ $t(filter.label) }}</b-button>
          </b-button-group>
          <b-button v-if="selectedPeople.length > 1" variant="danger" size="sm" @click="mergeSelected">
            {{ $t('people_merge_selected') }}
          </b-button>
        </div>

        <div v-if="items.length === 0" class="people-empty">{{ $t('people_empty') }}</div>
        <div v-else class="people-grid">
          <article v-for="item in items" :key="`${activeFilter}-${item.id}`" class="person-card"
            @click="activeFilter === 'review' ? showSource(item) : openPerson(item)">
            <b-form-checkbox v-if="activeFilter !== 'review'" class="person-select"
              :checked="selectedPeople.includes(item.id)" @change="togglePerson(item.id)" @click.native.stop />
            <div class="person-photo">
              <img v-if="cropUrl(item)" :src="cropUrl(item)" alt="" />
              <span v-else class="person-placeholder"><i class="bi bi-person-fill" /></span>
              <span v-if="item.assignment_state === 'review'" class="review-badge">{{ $t('people_review') }}</span>
            </div>
            <div class="person-meta">
              <strong>{{ item.name || (item.person && item.person.name) || $t('people_unnamed') }}</strong>
              <span>{{ item.face_count || 1 }} {{ $t('people_photos') }}</span>
            </div>
            <div v-if="activeFilter === 'review'" class="review-actions" @click.stop>
              <b-select :value="reviewTargets[item.id]" size="sm" @input="$set(reviewTargets, item.id, $event)">
                <option :value="null">{{ $t('people_choose_person') }}</option>
                <option v-for="person in allTargets" :key="person.id" :value="person.id">
                  {{ person.name || $t('people_unnamed') }}
                </option>
              </b-select>
              <b-button size="sm" variant="primary" :disabled="!reviewTargets[item.id]" @click="approveReview(item)">
                {{ $t('people_approve') }}
              </b-button>
              <b-button size="sm" variant="outline-secondary" @click="ignoreReview(item)">
                {{ $t('people_ignore_one') }}
              </b-button>
            </div>
          </article>
        </div>

        <div v-if="total > items.length" class="people-load-more">
          <b-button variant="outline-primary" @click="loadMore">{{ $t('people_load_more') }}</b-button>
        </div>
      </template>
    </template>

    <b-modal v-model="detailOpen" size="xl" :title="detail ? (detail.name || $t('people_unnamed')) : ''" hide-footer>
      <template v-if="detail">
        <div class="person-editor">
          <b-input v-model="editName" :placeholder="$t('people_name_placeholder')" />
          <b-button v-if="detail.status === 'candidate'" variant="primary" @click="confirmPerson">
            {{ $t('people_confirm_person') }}
          </b-button>
          <b-button v-else variant="outline-primary" @click="savePerson">{{ $t('apply') }}</b-button>
          <b-button variant="outline-secondary" @click="toggleHidden">
            {{ detail.status === 'hidden' ? $t('people_unhide') : $t('people_hide') }}
          </b-button>
        </div>

        <div v-if="selectedFaces.length" class="face-actions">
          <b-select v-model="moveTarget" size="sm">
            <option :value="null">{{ $t('people_detach') }}</option>
            <option v-for="person in moveTargets" :key="person.id" :value="person.id">{{ person.name || $t('people_unnamed') }}</option>
          </b-select>
          <b-button size="sm" variant="primary" @click="moveSelectedFaces">{{ $t('people_move') }}</b-button>
          <b-button size="sm" variant="outline-secondary" @click="setCover">{{ $t('people_set_cover') }}</b-button>
          <b-button size="sm" variant="outline-warning" @click="splitSelected">{{ $t('people_split') }}</b-button>
          <b-button size="sm" variant="outline-danger" @click="ignoreSelected">{{ $t('people_ignore') }}</b-button>
        </div>

        <div class="faces-grid">
          <button v-for="face in detail.faces.items" :key="face.id" class="face-tile"
            :class="{ selected: selectedFaces.includes(face.id) }" @click="toggleFace(face.id)">
            <img :src="face.crop_url" alt="" @dblclick.stop="showSource(face)" />
            <span>{{ face.media && (face.media.date || face.media.filename) }}</span>
          </button>
        </div>
        <div v-if="detail.faces.total > detail.faces.items.length" class="people-load-more">
          <b-button variant="outline-primary" @click="loadMoreFaces">{{ $t('people_load_more') }}</b-button>
        </div>
      </template>
    </b-modal>

    <b-modal v-model="sourceOpen" size="xl" :title="$t('people_source_photo')" hide-footer>
      <div v-if="sourceFace && sourceFace.media" class="source-photo">
        <img :src="sourceUrl(sourceFace)" alt="" />
        <span class="face-overlay" :style="overlayStyle(sourceFace)" />
      </div>
    </b-modal>

    <b-modal v-model="failureOpen" size="lg" :title="$t('people_failures_title')" hide-footer>
      <div v-if="failureLoading" class="people-loading"><b-spinner small /> {{ $t('people_loading') }}</div>
      <div v-else-if="failures.items.length === 0" class="people-empty">{{ $t('people_no_failures') }}</div>
      <div v-else class="failure-list">
        <article v-for="failure in failures.items" :key="failure.id">
          <div>
            <strong>{{ $t('people_job') }} #{{ failure.id }}</strong>
            <span>{{ $t('people_attempts') }}: {{ failure.attempts }} · {{ formatDuration(failure.duration_ms) }}</span>
          </div>
          <code>{{ failure.last_error }}</code>
        </article>
        <b-button v-if="failures.total > failures.items.length" variant="outline-secondary" @click="loadFailures(true)">
          {{ $t('people_load_more') }}
        </b-button>
      </div>
      <div v-if="failures.total" class="failure-actions">
        <b-button variant="primary" @click="retryFailures">{{ $t('people_retry_failed') }}</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import People from '../../js/model/people.js'

export default {
  data() {
    return {
      model: null,
      loading: true,
      error: null,
      operationError: null,
      status: null,
      activeFilter: 'review',
      items: [],
      total: 0,
      limit: 50,
      selectedPeople: [],
      actions: [],
      allTargets: [],
      reviewTargets: {},
      detailOpen: false,
      detail: null,
      editName: '',
      selectedFaces: [],
      moveTarget: null,
      sourceOpen: false,
      sourceFace: null,
      failureOpen: false,
      failureLoading: false,
      failures: { total: 0, items: [] },
      poller: null,
    }
  },
  computed: {
    filters() {
      return [
        { id: 'review', label: 'people_needs_review' },
        { id: 'candidate', label: 'people_unconfirmed' },
        { id: 'confirmed', label: 'people_confirmed' },
        { id: 'hidden', label: 'people_hidden' },
      ]
    },
    selectedPeopleObjects() { return this.items.filter(item => this.selectedPeople.includes(item.id)) },
    moveTargets() { return this.allTargets.filter(item => item.id !== this.detail?.id) },
    lastAction() { return this.actions.find(action => action.undone_at == null) },
    activeJobs() { return Math.max(1, this.status.jobs.pending + this.status.jobs.running + this.status.jobs.completed) },
    completedJobs() { return this.status.jobs.completed },
    stateLabel() {
      if (this.status.jobs.paused) return this.$t('people_paused')
      if (this.status.jobs.failed) return this.$t('people_error_state')
      if (this.status.jobs.pending || this.status.jobs.running) return this.$t('people_indexing')
      return this.$t('people_ready')
    },
    stateClass() {
      if (this.status.jobs.paused) return 'paused'
      if (this.status.jobs.failed) return 'error'
      if (this.status.jobs.pending || this.status.jobs.running) return 'indexing'
      return 'ready'
    },
  },
  created() {
    this.model = new People({ http: this.$http })
    this.refresh()
    this.poller = setInterval(() => {
      if (this.status?.jobs?.pending || this.status?.jobs?.running) this.refreshStatus()
    }, 3000)
  },
  beforeDestroy() { clearInterval(this.poller) },
  methods: {
    errorMessage(error) { return error.response?.data?.error || error.message || String(error) },
    async runOperation(operation) {
      this.operationError = null
      try {
        await operation()
        return true
      } catch (error) {
        this.operationError = this.errorMessage(error)
        return false
      }
    },
    async refresh() {
      this.loading = true
      this.error = null
      try {
        await this.refreshStatus()
        if (this.status.jobs.enabled && this.status.capability.state === 'ready') {
          await Promise.all([this.loadItems(), this.loadActions(), this.loadTargets()])
        }
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.loading = false
      }
    },
    async refreshStatus() { this.status = await this.model.status() },
    async loadActions() { this.actions = await this.model.actions() },
    async enable() {
      await this.runOperation(async () => {
        await this.model.enable(true)
        await this.refresh()
        await this.analyze('all')
      })
    },
    async pause(paused) {
      await this.runOperation(async () => {
        await this.model.pause(paused)
        await this.refreshStatus()
      })
    },
    async analyze(operation) {
      await this.runOperation(async () => {
        await this.model.analyze(operation)
        await this.refresh()
      })
    },
    async confirmRebuild() {
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('people_rebuild_confirm'), { okVariant: 'danger' })
      if (confirmed) await this.analyze('rebuild')
    },
    async confirmErase() {
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('people_erase_confirm'), {
        okTitle: this.$t('people_erase'),
        okVariant: 'danger',
      })
      if (!confirmed) return
      await this.runOperation(async () => {
        await this.model.erase()
        this.items = []
        this.allTargets = []
        await this.refresh()
      })
    },
    async confirmDisable() {
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('people_disable_confirm'))
      if (!confirmed) return
      await this.runOperation(async () => {
        await this.model.enable(false)
        await this.refresh()
      })
    },
    async selectFilter(filter) {
      this.activeFilter = filter
      this.selectedPeople = []
      await this.loadItems()
    },
    async loadItems(append=false) {
      const offset = append ? this.items.length : 0
      const data = this.activeFilter === 'review'
        ? await this.model.faces('review', offset, this.limit)
        : await this.model.persons(this.activeFilter, offset, this.limit)
      this.items = append ? [...this.items, ...data.items] : data.items
      this.total = data.total
      if (this.activeFilter === 'review') {
        for (const face of data.items) this.$set(this.reviewTargets, face.id, face.person_id || null)
      }
    },
    async loadTargets() {
      const loadStatus = async status => {
        const items = []
        let offset = 0
        let total = 0
        do {
          const page = await this.model.persons(status, offset, 200)
          total = page.total
          items.push(...page.items)
          offset = items.length
        } while (offset < total)
        return items
      }
      const [confirmed, candidates] = await Promise.all([loadStatus('confirmed'), loadStatus('candidate')])
      this.allTargets = [...confirmed, ...candidates]
    },
    loadMore() { return this.loadItems(true) },
    cropUrl(item) {
      if (item.crop_url) return item.crop_url
      return item.representative_face_id ? `/control/people/faces/${item.representative_face_id}/crop` : null
    },
    togglePerson(id) {
      this.selectedPeople = this.selectedPeople.includes(id)
        ? this.selectedPeople.filter(value => value !== id)
        : [...this.selectedPeople, id]
    },
    async openPerson(person) {
      this.detail = await this.model.person(person.id)
      this.editName = this.detail.name
      this.selectedFaces = []
      this.moveTarget = null
      this.detailOpen = true
    },
    toggleFace(id) {
      this.selectedFaces = this.selectedFaces.includes(id)
        ? this.selectedFaces.filter(value => value !== id)
        : [...this.selectedFaces, id]
    },
    async reloadDetail() {
      const id = this.detail.id
      this.detail = await this.model.person(id, 0, Math.max(100, this.detail.faces.items.length))
      this.editName = this.detail.name
      this.selectedFaces = []
      await Promise.all([this.refreshStatus(), this.loadItems(), this.loadTargets(), this.loadActions()])
    },
    async loadMoreFaces() {
      const next = await this.model.person(this.detail.id, this.detail.faces.items.length, 100)
      this.detail.faces.items.push(...next.faces.items)
    },
    async confirmPerson() {
      await this.runOperation(async () => {
        await this.model.confirm(this.detail, this.editName)
        await this.reloadDetail()
      })
    },
    async savePerson() {
      await this.runOperation(async () => {
        await this.model.update(this.detail, { name: this.editName })
        await this.reloadDetail()
      })
    },
    async toggleHidden() {
      await this.runOperation(async () => {
        await this.model.update(this.detail, { hidden: this.detail.status !== 'hidden' })
        await this.reloadDetail()
      })
    },
    async moveSelectedFaces() {
      const target = this.moveTarget == null ? null : this.moveTargets.find(person => person.id === this.moveTarget)
      await this.runOperation(async () => {
        await this.model.move(this.selectedFaces, target)
        await this.reloadDetail()
      })
    },
    async ignoreSelected() {
      await this.runOperation(async () => {
        await this.model.ignore(this.selectedFaces)
        await this.reloadDetail()
      })
    },
    async splitSelected() {
      await this.runOperation(async () => {
        await this.model.split(this.detail, this.selectedFaces)
        await this.reloadDetail()
      })
    },
    async setCover() {
      if (this.selectedFaces.length !== 1) return
      await this.runOperation(async () => {
        await this.model.cover(this.detail, this.selectedFaces[0])
        await this.reloadDetail()
      })
    },
    async mergeSelected() {
      const people = this.selectedPeopleObjects
      if (people.length < 2) return
      const hasConfirmed = people.slice(1).some(person => person.status === 'confirmed')
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$t(hasConfirmed ? 'people_merge_confirmed' : 'people_merge_confirm'), { okVariant: 'danger' })
      if (!confirmed) return
      await this.runOperation(async () => {
        await this.model.merge(people[0], people.slice(1), hasConfirmed)
        this.selectedPeople = []
        await this.refresh()
      })
    },
    async approveReview(face) {
      const target = this.allTargets.find(person => person.id === this.reviewTargets[face.id])
      if (!target) return
      await this.runOperation(async () => {
        await this.model.move([face.id], target)
        await this.refresh()
      })
    },
    async ignoreReview(face) {
      await this.runOperation(async () => {
        await this.model.ignore([face.id])
        await this.refresh()
      })
    },
    async undoLast() {
      if (!this.lastAction) return
      await this.runOperation(async () => {
        await this.model.undo(this.lastAction.id)
        await this.refresh()
        if (this.detailOpen && this.detail) await this.reloadDetail()
      })
    },
    async openFailures() {
      this.failureOpen = true
      await this.loadFailures()
    },
    async loadFailures(append=false) {
      this.failureLoading = true
      try {
        const offset = append ? this.failures.items.length : 0
        const data = await this.model.failures(offset, 50)
        this.failures = {
          ...data,
          items: append ? [...this.failures.items, ...data.items] : data.items,
        }
      } finally {
        this.failureLoading = false
      }
    },
    async retryFailures() {
      await this.runOperation(async () => {
        await this.model.analyze('failed')
        this.failureOpen = false
        await this.refresh()
      })
    },
    showSource(face) { this.sourceFace = face; this.sourceOpen = true },
    sourceUrl(face) { return face.source_url || `/control/people/faces/${face.id}/source` },
    formatDuration(milliseconds) {
      if (milliseconds == null) return '—'
      if (milliseconds < 1000) return `${milliseconds} ms`
      const seconds = Math.round(milliseconds / 1000)
      if (seconds < 60) return `${seconds} s`
      const minutes = Math.floor(seconds / 60)
      const remaining = seconds % 60
      return remaining ? `${minutes} m ${remaining} s` : `${minutes} m`
    },
    overlayStyle(face) {
      return { left: `${face.x * 100}%`, top: `${face.y * 100}%`, width: `${face.width * 100}%`, height: `${face.height * 100}%` }
    },
  },
}
</script>

<style scoped>
.people-admin { min-height: 480px; }
.people-loading, .people-empty { padding: 5rem 1rem; text-align: center; color: var(--ghosted-text-color); }
.people-onboarding { max-width: 620px; margin: 4rem auto; text-align: center; padding: 3rem; border: 1px solid rgba(127,127,127,.25); border-radius: 18px; background: rgba(127,127,127,.06); }
.people-onboarding-icon { width: 84px; height: 84px; margin: 0 auto 1.5rem; display: grid; place-items: center; border-radius: 50%; font-size: 2.2rem; color: white; background: var(--highlight-color); }
.people-onboarding p { font-size: 1.05rem; line-height: 1.6; }
.privacy-note { color: var(--ghosted-text-color); }
.people-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1.5rem; }
.people-toolbar h3 { display: inline; margin-right: .7rem; }
.people-actions { display: flex; gap: .5rem; flex-wrap: wrap; justify-content: flex-end; }
.people-state { border-radius: 99px; padding: .25rem .65rem; font-size: .75rem; text-transform: uppercase; letter-spacing: .06em; }
.people-state.ready { color: #25864b; background: rgba(37,134,75,.15); }
.people-state.indexing { color: #a46a00; background: rgba(255,171,0,.17); }
.people-state.paused, .people-state.error { color: #a12828; background: rgba(190,50,50,.15); }
.people-metrics { display: grid; grid-template-columns: repeat(4, 1fr); gap: .75rem; margin-bottom: 1rem; }
.people-metrics button { border: 1px solid rgba(127,127,127,.25); border-radius: 12px; background: rgba(127,127,127,.05); padding: 1rem; color: inherit; text-align: left; }
.people-metrics strong { display: block; font-size: 1.8rem; }
.people-metrics span { color: var(--ghosted-text-color); }
.people-progress { display: grid; gap: .4rem; margin: 1.25rem 0; font-size: .85rem; color: var(--ghosted-text-color); }
.people-diagnostics { display: flex; flex-wrap: wrap; gap: .5rem 1.5rem; margin: .75rem 0; color: var(--ghosted-text-color); font-size: .85rem; }
.people-failure-alert { display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
.people-filterbar { display: flex; justify-content: space-between; align-items: center; margin: 1.5rem 0 1rem; }
.people-grid, .faces-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 1rem; }
.person-card { position: relative; overflow: hidden; border: 1px solid rgba(127,127,127,.22); border-radius: 12px; cursor: pointer; background: rgba(127,127,127,.04); transition: transform .15s ease, border-color .15s ease; }
.person-card:hover { transform: translateY(-2px); border-color: var(--highlight-color); }
.person-select { position: absolute; z-index: 2; top: .5rem; left: .6rem; padding: .25rem .25rem .25rem 1.5rem; border-radius: 5px; background: rgba(0,0,0,.55); }
.person-photo { position: relative; aspect-ratio: 1; background: rgba(0,0,0,.2); }
.person-photo img { width: 100%; height: 100%; object-fit: cover; }
.person-placeholder { display: grid; width: 100%; height: 100%; place-items: center; font-size: 3rem; color: var(--ghosted-text-color); }
.review-badge { position: absolute; right: .5rem; bottom: .5rem; padding: .2rem .45rem; border-radius: 99px; color: white; background: #d07b00; font-size: .7rem; }
.person-meta { display: grid; padding: .75rem; }
.person-meta span { color: var(--ghosted-text-color); font-size: .8rem; }
.review-actions { display: grid; grid-template-columns: 1fr auto; gap: .4rem; padding: 0 .75rem .75rem; }
.review-actions .custom-select { grid-column: 1 / -1; min-width: 0; }
.people-load-more { text-align: center; margin: 1.5rem; }
.person-editor, .face-actions { display: flex; gap: .5rem; margin-bottom: 1rem; }
.person-editor .form-control { flex: 1; }
.face-actions .custom-select { max-width: 250px; }
.face-tile { overflow: hidden; padding: 0; border: 3px solid transparent; border-radius: 9px; background: rgba(127,127,127,.08); color: inherit; }
.face-tile.selected { border-color: var(--highlight-color); }
.face-tile img { width: 100%; aspect-ratio: 1; object-fit: cover; }
.face-tile span { display: block; padding: .4rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: .7rem; }
.source-photo { position: relative; width: fit-content; max-width: 100%; margin: auto; }
.source-photo img { display: block; max-width: 100%; max-height: 75vh; }
.face-overlay { position: absolute; border: 3px solid #ffcc00; box-shadow: 0 0 0 9999px rgba(0,0,0,.25); pointer-events: none; }
.failure-list { display: grid; gap: .75rem; }
.failure-list article { display: grid; gap: .5rem; padding: .85rem; border: 1px solid rgba(127,127,127,.22); border-radius: 9px; }
.failure-list article > div { display: flex; justify-content: space-between; gap: 1rem; }
.failure-list article span { color: var(--ghosted-text-color); font-size: .8rem; }
.failure-list code { overflow-wrap: anywhere; color: inherit; white-space: pre-wrap; }
.failure-actions { display: flex; justify-content: flex-end; margin-top: 1rem; }
@media (max-width: 800px) { .people-toolbar { align-items: flex-start; flex-direction: column; } .people-metrics { grid-template-columns: repeat(2, 1fr); } }
</style>
